import { Head, router } from "@inertiajs/react"
import { Paperclip } from "lucide-react"
import { useState } from "react"

import {
  HpiDataTable,
  HpiDateFilterBar,
  HpiFilterSelect,
  HpiPagination,
  HpiStatsBar,
} from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface ExpenseItem {
  id: number
  date: string | null
  employee: string | null
  case_name: string | null
  case_id: number | null
  expense_type: string | null
  description: string | null
  amount: number
  paid_by: string | null
  approved: boolean
  paid: boolean
  paid_at: string | null
  paid_reference: string | null
  has_receipt: boolean
  receipt_url: string | null
}

interface Props {
  expenses: ExpenseItem[]
  start_date: string
  end_date: string
  employees: { id: number; name: string }[]
  expense_types: { id: number; name: string }[]
  payment_methods: { value: string; label: string }[]
  selected_employee_id: number | null
  selected_expense_type_id: number | null
  selected_payment_method: string | null
  totals: {
    count: number
    total_amount: number
  }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  "HPI AmEx": "Company AmEx",
  Cash: "Reimburse",
  Vendor: "Vendor",
}

/* ── Columns ───────────────────────────────────────────── */

const PER_PAGE = 15

const columns: HpiColumn<ExpenseItem>[] = [
  {
    header: "Date",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{formatDate(row.date)}</div>,
  },
  {
    header: "Employee",
    render: (row) => (
      <div>
        <div className="text-sm">{row.employee ?? "-"}</div>
        {row.paid_by && (
          <div className="text-hpi-med-gray text-xs">
            {PAYMENT_METHOD_LABELS[row.paid_by] ?? row.paid_by}
          </div>
        )}
      </div>
    ),
  },
  {
    header: "Case",
    render: (row) => <div className="text-sm">{row.case_name ?? "-"}</div>,
  },
  {
    header: "Type",
    width: "max-w-[120px]",
    render: (row) => <div className="text-sm">{row.expense_type ?? "-"}</div>,
  },
  {
    header: "Amount",
    width: "max-w-[100px]",
    render: (row) => (
      <div className="text-right text-sm font-medium">
        {formatCurrency(row.amount)}
      </div>
    ),
  },
  {
    header: "",
    width: "max-w-[40px]",
    render: (row) => {
      if (row.receipt_url) {
        return (
          <a
            href={row.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-hpi-active-blue hover:text-hpi-blue"
            title="View receipt"
            onClick={(e) => e.stopPropagation()}
          >
            <Paperclip className="h-4 w-4" />
          </a>
        )
      }
      if (row.has_receipt) {
        return (
          <span title="Receipt (legacy)">
            <Paperclip className="text-hpi-med-gray h-4 w-4" />
          </span>
        )
      }
      return null
    },
  },
  {
    header: "Status",
    width: "max-w-[120px]",
    render: (row) => {
      if (row.paid) return <div className="text-hpi-green text-sm">Paid</div>
      if (row.approved)
        return <div className="text-hpi-active-blue text-sm">Approved</div>
      return <div className="text-hpi-yellow text-sm">Submitted</div>
    },
  },
  {
    header: "Ref #",
    width: "max-w-[100px]",
    render: (row) => <div className="text-sm">{row.paid_reference ?? "-"}</div>,
  },
]

/* ── Main Component ────────────────────────────────────── */

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Accounting", href: "/accounting/expenses" },
  { title: "Expense Search", href: "/accounting/reports/expenses" },
]

export default function ExpenseSearch({
  expenses,
  start_date,
  end_date,
  employees,
  expense_types,
  payment_methods,
  selected_employee_id,
  selected_expense_type_id,
  selected_payment_method,
  totals,
}: Props) {
  const [startDate, setStartDate] = useState(start_date)
  const [endDate, setEndDate] = useState(end_date)
  const [employeeId, setEmployeeId] = useState<string>(
    selected_employee_id?.toString() ?? "",
  )
  const [expenseTypeId, setExpenseTypeId] = useState<string>(
    selected_expense_type_id?.toString() ?? "",
  )
  const [paymentMethod, setPaymentMethod] = useState<string>(
    selected_payment_method ?? "",
  )
  const [page, setPage] = useState(1)

  const totalPages = Math.ceil(expenses.length / PER_PAGE)
  const paginated = expenses.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const handleUpdate = () => {
    const params: Record<string, string> = {
      start_date: startDate,
      end_date: endDate,
    }
    if (employeeId) params.employee_id = employeeId
    if (expenseTypeId) params.expense_type_id = expenseTypeId
    if (paymentMethod) params.payment_method = paymentMethod
    router.get("/accounting/reports/expenses", params, { preserveState: true })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Expense Search" />

      <div className="p-6">
        <HpiDateFilterBar
          startDate={startDate}
          onStartDateChange={setStartDate}
          endDate={endDate}
          onEndDateChange={setEndDate}
          onUpdate={handleUpdate}
          className="mb-6"
        >
          <HpiFilterSelect
            label="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            options={employees.map((e) => ({ value: e.id, label: e.name }))}
            placeholder="All Employees"
          />
          <HpiFilterSelect
            label="Expense Type"
            value={expenseTypeId}
            onChange={setExpenseTypeId}
            options={expense_types.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="All Types"
          />
          <HpiFilterSelect
            label="Payment Method"
            value={paymentMethod}
            onChange={setPaymentMethod}
            options={payment_methods.map((pm) => ({
              value: pm.value,
              label: pm.label,
            }))}
            placeholder="All Methods"
          />
        </HpiDateFilterBar>

        <HpiStatsBar
          stats={[
            { label: "Total", subLabel: "Expenses", value: totals.count },
            {
              label: "Total",
              subLabel: "Amount",
              value: formatCurrency(totals.total_amount),
            },
          ]}
          className="mb-6"
        />

        <HpiDataTable<ExpenseItem>
          title="Expense Search Results"
          columns={columns}
          data={paginated}
          keyFn={(row) => row.id}
          rowHref={(row) =>
            row.case_id ? `/cases/${row.case_id}/expenses/${row.id}` : undefined
          }
          pagination={
            expenses.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={expenses.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {expenses.length === 0 && (
          <div className="border-hpi-light-gray text-hpi-med-blue mt-4 rounded-lg border bg-white p-8 text-center">
            No expenses found for the selected criteria.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
